import { buildApiUrl, API_CONFIG } from '../config';
import { clearPermissionsCache } from './permissions';

// Hilfsfunktion um alle Tokens zu löschen
export const clearAllTokens = (): void => {
  console.log('🧹 Lösche alle Tokens...');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('loggedIn');
  localStorage.removeItem('selectedOrgID');
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('loggedIn');
  sessionStorage.removeItem('selectedOrgID');
  
  // Clear permissions cache
  clearPermissionsCache();
  
  console.log('✅ Alle Tokens gelöscht');
};

// Hilfsfunktion um den Refresh Token zu bekommen
export const getRefreshToken = (): string | null => {
  // Prüfe localStorage zuerst (für Persistenz)
  let refreshToken = localStorage.getItem('refreshToken');
  
  // Falls nicht in localStorage, prüfe sessionStorage
  if (!refreshToken) {
    refreshToken = sessionStorage.getItem('refreshToken');
  }
  
  // Bereinige ungültige Tokens
  if (refreshToken === 'undefined' || refreshToken === 'null' || refreshToken === '') {
    console.log('🧹 Ungültigen Refresh-Token gefunden - Benutzer wird ausgeloggt');
    clearAllTokens(); // Lösche ALLE Tokens komplett
    return null;
  }
  
  return refreshToken;
};

// Token automatisch erneuern
export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    // Prüfe ob wir online sind
    if (!navigator.onLine) {
      console.log('🌐 Offline - kann Token nicht aktualisieren');
      return false;
    }
    console.log('🍪 Verfügbare Cookies:', document.cookie);
    console.log('🍪 Refresh-Endpoint:', buildApiUrl(API_CONFIG.ENDPOINTS.REFRESH));

    // Hole den Refresh-Token aus dem localStorage ODER verwende HttpOnly Cookie
    let refreshToken = getRefreshToken();
    
    // Falls kein Token in localStorage, versuche HttpOnly Cookie zu verwenden
    if (!refreshToken) {
      console.log('🔄 Kein Refresh-Token in localStorage, verwende HttpOnly Cookie');
      
      // Prüfe zuerst, ob wir überhaupt Cookies haben
      console.log('🍪 Alle verfügbaren Cookies:', document.cookie);
      console.log('🍪 Cookie-Länge:', document.cookie.length);
      console.log('🍪 Aktuelle Domain:', window.location.hostname);
      console.log('🍪 Aktuelle URL:', window.location.href);
      
      if (!document.cookie || document.cookie.trim() === '') {
        console.log('🍪 Keine Cookies gefunden, kann nicht refreshen');
        console.log('🔍 MÖGLICHE URSACHEN:');
        console.log('   - HttpOnly Cookie ist abgelaufen');
        console.log('   - Cookie-Domain/Path stimmt nicht überein');
        console.log('   - Browser blockiert Cookies');
        console.log('   - Server setzt Cookie nicht korrekt');
        return false;
      }
      
      // Sende Request OHNE Body, lass den Server den HttpOnly Cookie verwenden
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REFRESH), {
        method: 'POST',
        credentials: 'include', // Wichtig: Sendet automatisch alle Cookies mit
        headers: {
          'Content-Type': 'application/json',
        }
        // KEIN Body - der Server verwendet den HttpOnly Cookie
      });

      console.log('🔄 Refresh-Response Status (HttpOnly):', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Token erfolgreich aktualisiert (HttpOnly)');
        
        if (data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('loggedIn', '1'); // Wichtig: loggedIn Status setzen
          console.log('💾 Neuer Access-Token gespeichert');
          
          // Falls der Server einen neuen Refresh-Token zurückgibt, speichere ihn
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
            sessionStorage.setItem('refreshToken', data.refreshToken);
            console.log('💾 Neuer Refresh-Token gespeichert');
          }
          
          return true;
        }
      }
      
      // Bei 401-Fehlern nicht sofort aufgeben, könnte ein temporäres Problem sein
      if (response.status === 401) {
        console.log('⚠️ HttpOnly Cookie Refresh mit 401 - könnte temporär sein, versuche es später erneut');
        // Nicht alle Tokens löschen, da der Cookie möglicherweise noch gültig ist
        return false;
      }
      
      console.log('❌ HttpOnly Cookie Refresh fehlgeschlagen');
      return false;
    }

    // Fallback: Verwende den Token aus localStorage
    console.log('🔄 getRefreshToken() zurückgegeben:', refreshToken);
    console.log('🔄 refreshToken type:', typeof refreshToken);
    console.log('🔄 refreshToken truthy check:', !!refreshToken);
    console.log('🔄 refreshToken === null:', refreshToken === null);
    console.log('🔄 refreshToken === undefined:', refreshToken === undefined);
    console.log('🔄 refreshToken === "undefined":', refreshToken === 'undefined');

    if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
      console.log('❌ Kein gültiger Refresh-Token verfügbar');
      console.log('🔄 Kein Redirect - lass die App das handhaben');
      return false;
    }

    // Prüfe ob der Refresh-Token noch gültig ist
    try {
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const now = Date.now() / 1000;
      
      console.log('🔄 Refresh-Token Payload:', payload);
      console.log('🔄 Refresh-Token läuft ab:', new Date(payload.exp * 1000).toLocaleString('de-DE'));
      console.log('🔄 Aktuelle Zeit:', new Date(now * 1000).toLocaleString('de-DE'));
      console.log('🔄 Refresh-Token gültig:', payload.exp > now);
      
      if (payload.exp <= now) {
        console.log('❌ Refresh-Token ist abgelaufen - Benutzer wird ausgeloggt');
        clearAllTokens();
        return false;
      }
    } catch (error) {
      console.log('❌ Refresh-Token ist ungültig (nicht decodierbar) - Benutzer wird ausgeloggt');
      clearAllTokens();
      return false;
    }

    console.log('🔄 Refresh-Token verfügbar, Länge:', refreshToken.length);
    console.log('🔄 Sende Request-Body:', JSON.stringify({ refreshToken }));
    
    // Debug: Zeige den aktuellen Access-Token
    const currentAccessToken = getAccessToken();
    if (currentAccessToken) {
      try {
        const payload = JSON.parse(atob(currentAccessToken.split('.')[1]));
        console.log('🔍 Aktueller Access-Token Payload:', payload);
        console.log('🔍 Access-Token läuft ab:', new Date(payload.exp * 1000).toLocaleString('de-DE'));
        console.log('🔍 Access-Token gültig:', payload.exp > Date.now() / 1000);
      } catch (error) {
        console.log('❌ Access-Token ist nicht decodierbar:', error);
      }
    } else {
      console.log('❌ Kein Access-Token vorhanden');
    }

    const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REFRESH), {
      method: 'POST',
      credentials: 'include', // Wichtig: Sendet automatisch alle Cookies mit
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }) // Token im Body senden
    });

    console.log('🔄 Refresh-Response Status:', response.status);
    console.log('🔄 Refresh-Response Headers:', Object.fromEntries(response.headers.entries()));

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Token erfolgreich aktualisiert');
      console.log('🔍 Response-Daten:', data);
      console.log('🔍 accessToken vorhanden:', !!data.accessToken);
      console.log('🔍 refreshToken vorhanden:', !!data.refreshToken);
      
      if (data.accessToken) {
        // Speichere den neuen Access-Token
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('loggedIn', '1'); // Wichtig: loggedIn Status setzen
        console.log('💾 Neuer Access-Token gespeichert');
        
        // WICHTIG: Nach dem Token-Refresh sicherstellen, dass die aktuell ausgewählte Organisation verwendet wird
        const selectedOrgID = localStorage.getItem('selectedOrgID');
        if (selectedOrgID) {
          console.log('🏢 Nach Token-Refresh: Verwende ausgewählte Organisation:', selectedOrgID);
          // Stelle sicher, dass alle nachfolgenden API-Calls diese Organisation verwenden
          localStorage.setItem('currentOrgID', selectedOrgID);
        }
        
        // Speichere auch den neuen Refresh-Token falls vorhanden
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
          sessionStorage.setItem('refreshToken', data.refreshToken);
          console.log('💾 Neuer Refresh-Token gespeichert');
          
          // Debug: Prüfe ob er wirklich gespeichert wurde
          const savedRefreshToken = localStorage.getItem('refreshToken');
          console.log('🔍 Gespeicherter Refresh-Token:', savedRefreshToken ? 'JA' : 'NEIN');
          console.log('🔍 Refresh-Token Länge:', savedRefreshToken ? savedRefreshToken.length : '0');
        } else {
          console.log('⚠️ Kein neuer Refresh-Token in der Response');
        }
        
        return true;
      } else {
        console.log('❌ Kein Access-Token in der Response');
        return false;
      }
    } else {
      const errorData = await response.json().catch(() => ({}));
      console.log('❌ Token-Refresh fehlgeschlagen:', response.status, errorData);
      console.log('🔍 Refresh-Endpoint:', buildApiUrl(API_CONFIG.ENDPOINTS.REFRESH));
      console.log('🔍 Refresh-Token Länge:', refreshToken.length);
      console.log('🔍 Refresh-Token gültig bis:', new Date(JSON.parse(atob(refreshToken.split('.')[1])).exp * 1000).toLocaleString('de-DE'));
      
      // Wenn der Backend explizit "Invalid refresh token" zurückgibt, 
      // Benutzer ausloggen
      if (errorData.message === 'Invalid refresh token') {
        console.log('🔄 Ungültiger Refresh-Token - Benutzer wird ausgeloggt');
        clearAllTokens();
        return false;
      }
      
      return false;
    }
  } catch (error) {
    console.error('❌ Fehler beim Token-Refresh:', error);
    return false;
  }
};

// Hilfsfunktion um zu prüfen, ob Token bald abläuft (5 Minuten vorher)
export const isTokenExpiringSoon = (): boolean => {
  const accessToken = getAccessToken();
  
  if (!accessToken) {
    return false;
  }
  
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const now = Date.now() / 1000;
    const fiveMinutes = 5 * 60; // 5 Minuten in Sekunden
    
    return (payload.exp - now) < fiveMinutes;
  } catch (error) {
    return false;
  }
};

// Erweiterte Token-Validierung für Offline-Modus
export const isTokenValidForOffline = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    const offlineGracePeriod = 24 * 60 * 60; // 24 Stunden Offline-Grace-Periode
    
    return (payload.exp + offlineGracePeriod) > now;
  } catch (error) {
    return false;
  }
};

// Intelligente Token-Validierung (Online vs Offline)
export const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    
    if (navigator.onLine) {
      // Online: Normale Token-Validierung
      return payload.exp > now;
    } else {
      // Offline: Erweiterte Token-Validierung
      return isTokenValidForOffline(token);
    }
  } catch (error) {
    return false;
  }
};

// Lock für Token-Refresh um Race Conditions zu vermeiden
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
let refreshAttempts = 0;

// Reset refresh attempts on page load
if (typeof window !== 'undefined') {
  refreshAttempts = 0;
}

// Wrapper für fetch mit automatischer Token-Behandlung
export const apiFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  // Verwende die async Version von getAccessToken für automatischen Refresh
  const accessToken = await getAccessTokenAsync();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    console.log('🔍 API-Call mit Access-Token, Länge:', accessToken.length);
    
    // Debug: Zeige Token-Info
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      console.log('🔍 Access-Token Payload:', payload);
      console.log('🔍 Access-Token läuft ab:', new Date(payload.exp * 1000).toLocaleString('de-DE'));
      console.log('🔍 Access-Token gültig:', payload.exp > Date.now() / 1000);
    } catch (error) {
      console.log('❌ Access-Token ist nicht decodierbar:', error);
    }
  } else {
    console.log('❌ Kein Access-Token für API-Call');
  }

  let response = await fetch(url, {
    ...options,
    // Cookies (inkl. HttpOnly Refresh-Cookie) immer mitsenden
    credentials: 'include',
    headers,
  });

  // Bei 401-Fehler versuchen Token zu erneuern und Request zu wiederholen
  if (response.status === 401) {
    console.log('🔄 401-Fehler: Versuche Token-Refresh...');
    
    // Prüfe ob bereits ein Refresh läuft
    if (isRefreshing && refreshPromise) {
      console.log('⏳ Token-Refresh bereits im Gange, warte...');
      const refreshSuccess = await refreshPromise;
      
      if (refreshSuccess) {
        // Nach dem Refresh den Request mit neuem Token wiederholen
        const newAccessToken = await getAccessTokenAsync();
        if (newAccessToken) {
          headers['Authorization'] = `Bearer ${newAccessToken}`;
          console.log('✅ Token-Refresh abgeschlossen, wiederhole Request...');
          response = await fetch(url, {
            ...options,
            headers,
          });
        }
      }
      return response;
    }
    
    // Prüfe ob wir bereits zu viele Refresh-Versuche gemacht haben
    if (refreshAttempts >= 3) {
      console.log('❌ Zu viele Refresh-Versuche - Benutzer wird ausgeloggt');
      console.log('🔍 Aktuelle refreshAttempts:', refreshAttempts);
      
      // Benutzer ausloggen und zur Login-Seite weiterleiten
      logout();
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
      return response;
    }
    
    // Refresh-Versuch zählen
    refreshAttempts++;
    console.log(`🔄 Refresh-Versuch ${refreshAttempts}/3`);
    
    // Lock setzen und Token-Refresh starten
    isRefreshing = true;
    refreshPromise = refreshAccessToken();
    
    try {
      const refreshSuccess = await refreshPromise;
      
      if (refreshSuccess) {
        // Refresh-Versuche zurücksetzen
        refreshAttempts = 0;
        console.log('✅ Token-Refresh erfolgreich, Versuche zurückgesetzt');
        
        // WICHTIG: Nach dem Token-Refresh sicherstellen, dass die aktuell ausgewählte Organisation verwendet wird
        const selectedOrgID = localStorage.getItem('selectedOrgID');
        if (selectedOrgID) {
          console.log('🏢 Nach Token-Refresh: Verwende ausgewählte Organisation:', selectedOrgID);
          // Stelle sicher, dass alle nachfolgenden API-Calls diese Organisation verwenden
          localStorage.setItem('currentOrgID', selectedOrgID);
        }
        
        // Request mit neuem Token wiederholen
        const newAccessToken = await getAccessTokenAsync();
        if (newAccessToken) {
          headers['Authorization'] = `Bearer ${newAccessToken}`;
          console.log('✅ Token-Refresh erfolgreich, wiederhole Request...');
          response = await fetch(url, {
            ...options,
            headers,
          });
        }
      } else {
        console.log('❌ Token-Refresh fehlgeschlagen - Benutzer wird ausgeloggt');
        
        // Benutzer ausloggen und zur Login-Seite weiterleiten
        logout();
        setTimeout(() => {
          console.log('🚪 Weiterleitung zur Login-Seite...');
          window.location.href = '/login';
        }, 1000);
      }
    } finally {
      // Lock freigeben
      isRefreshing = false;
      refreshPromise = null;
    }
  }

  return response;
};

// Synchronous version for quick checks (doesn't trigger refresh)
export const isAuthenticatedSync = (): boolean => {
  // Prüfe localStorage zuerst (für Persistenz)
  let accessToken = localStorage.getItem('accessToken');
  let loggedIn = localStorage.getItem('loggedIn');
  
  // Falls nicht in localStorage, prüfe sessionStorage
  if (!accessToken || loggedIn !== '1') {
    accessToken = sessionStorage.getItem('accessToken');
    loggedIn = sessionStorage.getItem('loggedIn');
  }
  
  // Wenn kein Access-Token vorhanden ist, definitiv nicht eingeloggt
  if (!accessToken) {
    return false;
  }
  
  // Wenn Access-Token vorhanden ist, aber loggedIn Flag fehlt, 
  // setze es automatisch (kann bei Token-Refresh passieren)
  if (accessToken && loggedIn !== '1') {
    console.log('🔧 loggedIn Flag fehlt, setze es automatisch');
    localStorage.setItem('loggedIn', '1');
    loggedIn = '1';
  }
  
  // Token validieren (ohne Refresh - das macht der Interceptor bei 401)
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const now = Date.now() / 1000;
    
    // Wenn Token noch gültig ist, return true
    if (payload.exp > now) {
      return true;
    }
    
    // Wenn Token abgelaufen ist, aber wir einen Refresh-Token haben, 
    // return true damit der User nicht ausgeloggt wird
    // Der Interceptor wird den Token bei der nächsten API-Anfrage refreshen
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      console.log('🔄 Token abgelaufen, aber Refresh-Token vorhanden - User bleibt eingeloggt');
      return true;
    }
    
    // Kein Refresh-Token vorhanden, User muss sich neu einloggen
    return false;
  } catch (error) {
    return false;
  }
};

// Hauptfunktion für die Authentifizierungsprüfung
export const isAuthenticated = async (): Promise<boolean> => {
  console.log('🔍 isAuthenticated aufgerufen...');
  
  // Prüfe localStorage zuerst (für Persistenz)
  let accessToken = localStorage.getItem('accessToken');
  let loggedIn = localStorage.getItem('loggedIn');
  
  // Falls nicht in localStorage, prüfe sessionStorage
  if (!accessToken) {
    accessToken = sessionStorage.getItem('accessToken');
    loggedIn = sessionStorage.getItem('loggedIn');
  }
  
  console.log('📦 Token gefunden:', accessToken ? 'JA' : 'NEIN');
  console.log('📦 loggedIn Status:', loggedIn);
  
  // Wenn kein Token vorhanden, direkt false zurückgeben
  if (!accessToken || loggedIn !== '1') {
    console.log('❌ Kein Token oder nicht eingeloggt');
    return false;
  }
  
  // Token validieren
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const now = Date.now() / 1000;
    
    if (payload.exp <= now) {
      // Token abgelaufen, versuche Refresh (nur einmal!)
      console.log('🔄 Access-Token abgelaufen, versuche Refresh...');
      const refreshSuccess = await refreshAccessToken();
      if (refreshSuccess) {
        // Neuen Token holen und validieren
        accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          try {
            const newPayload = JSON.parse(atob(accessToken.split('.')[1]));
            return newPayload.exp > now;
          } catch (error) {
            console.log('❌ Neuer Token ist ungültig');
            return false;
          }
        }
        return false;
      } else {
        // Token-Refresh fehlgeschlagen - Benutzer ausloggen
        console.log('❌ Token-Refresh fehlgeschlagen - Benutzer wird ausgeloggt');
        logout();
        return false;
      }
    }
    
    return true;
  } catch (error) {
    // Ungültiger Token - NICHT nochmal Refresh versuchen
    console.log('❌ Ungültiger Token - kein weiterer Refresh-Versuch');
    return false;
  }
};

// Async Version für API-Calls mit Token-Refresh
export const isAuthenticatedAsync = async (): Promise<boolean> => {
  // Verwende die neue async Version von isAuthenticated
  return await isAuthenticated();
};

// Hilfsfunktion um den Access Token zu bekommen
export const getAccessToken = (): string | null => {
  // Prüfe localStorage zuerst (für Persistenz)
  let accessToken = localStorage.getItem('accessToken');
  
  // Falls nicht in localStorage, prüfe sessionStorage
  if (!accessToken) {
    accessToken = sessionStorage.getItem('accessToken');
  }
  
  return accessToken;
};

// Async Version von getAccessToken (ohne automatischen Refresh)
export const getAccessTokenAsync = async (): Promise<string | null> => {
  console.log('🔍 getAccessTokenAsync aufgerufen...');
  
  // Prüfe localStorage zuerst (für Persistenz)
  let accessToken = localStorage.getItem('accessToken');
  console.log('📦 Token in localStorage:', accessToken ? 'vorhanden' : 'nicht vorhanden');
  
  // Falls nicht in localStorage, prüfe sessionStorage
  if (!accessToken) {
    accessToken = sessionStorage.getItem('accessToken');
    console.log('📦 Token in sessionStorage:', accessToken ? 'vorhanden' : 'nicht vorhanden');
  }
  
  // Wenn kein Token vorhanden ist, versuche Refresh
  if (!accessToken) {
    console.log('🔄 Kein Access-Token gefunden, versuche Refresh...');
    const refreshSuccess = await refreshAccessToken();
    if (refreshSuccess) {
      // Nach erfolgreichem Refresh den neuen Token holen
      accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      console.log('✅ Token erfolgreich erneuert, neue Länge:', accessToken ? accessToken.length : 0);
    } else {
      console.log('⚠️ Token-Refresh fehlgeschlagen, aber das könnte normal sein');
      return null;
    }
  }
  
  // Token-Refresh wird nur bei 401-Response vom Interceptor gemacht
  // Hier geben wir einfach den Token zurück, auch wenn er abgelaufen ist
  return accessToken;
};

// Flag to prevent automatic refresh after logout
let preventAutoRefresh = false;

// Hilfsfunktion um sich abzumelden
export const logout = () => {
  console.log('🚪 Logout wird ausgeführt...');
  
  // Alle Refresh-Versuche zurücksetzen
  localStorage.removeItem('refreshAttempts');
  
  // Tour-Flags zurücksetzen
  localStorage.removeItem('tourStarted');
  localStorage.removeItem('tourState');
  
  // Alle Tokens löschen
  clearAllTokens();
  
  // Zusätzliche Daten löschen
  localStorage.removeItem('tutorialNeeded');
  localStorage.removeItem('locationsWithIds');
  localStorage.removeItem('standardStandorte');
  localStorage.removeItem('radioChannelsWithIds');
  localStorage.removeItem('standardFunkkanaele');
  localStorage.removeItem('operationsTypes');
  localStorage.removeItem('tasks');
  localStorage.removeItem('operationsTypesCache');
  localStorage.removeItem('selectedOrgID');
  
  // SessionStorage auch leeren
  sessionStorage.removeItem('tutorialNeeded');
  sessionStorage.removeItem('locationsWithIds');
  sessionStorage.removeItem('standardStandorte');
  sessionStorage.removeItem('radioChannelsWithIds');
  sessionStorage.removeItem('standardFunkkanaele');
  sessionStorage.removeItem('operationsTypes');
  sessionStorage.removeItem('tasks');
  sessionStorage.removeItem('operationsTypesCache');
  sessionStorage.removeItem('selectedOrgID');
  
  console.log('✅ Logout abgeschlossen - alle Daten gelöscht');
};

// Intelligente Online/Offline-Event-Handler
let isOnlineHandlerInitialized = false;

export const initializeOnlineOfflineHandlers = (): void => {
  if (isOnlineHandlerInitialized) return;
  
  // Online-Wiederkehr: Token-Refresh nachholen
  window.addEventListener('online', async () => {
    console.log('🌐 Online-Wiederkehr: Token-Refresh nachholen');
    
    // Prüfe ob Token abgelaufen ist und erneuere ihn
    const accessToken = getAccessToken();
    if (accessToken && !isTokenValid(accessToken)) {
      console.log('🔄 Token ist abgelaufen, erneuere bei Online-Wiederkehr...');
      await refreshAccessToken();
    }
    
    // Zeige Online-Benachrichtigung
    showOnlineNotification();
  });
  
  // Offline-Warnung
  window.addEventListener('offline', () => {
    console.log('📱 Offline-Modus aktiviert');
    showOfflineNotification();
  });
  
  isOnlineHandlerInitialized = true;
};

// Benachrichtigungen für Online/Offline-Status
const showOnlineNotification = (): void => {
  // Hier könnte eine Toast-Benachrichtigung angezeigt werden
  console.log('✅ Online-Verbindung wiederhergestellt');
};

const showOfflineNotification = (): void => {
  // Hier könnte eine Toast-Benachrichtigung angezeigt werden
  console.log('⚠️ Offline-Modus - Einige Funktionen eingeschränkt');
};

// Debug-Funktion zum Testen des Token-Refresh
export const testTokenRefresh = async (): Promise<void> => {
  console.log('🧪 Teste Token-Refresh...');
  
  // Aktuellen Token anzeigen
  const currentToken = getAccessToken();
  console.log('📋 Aktueller Token:', currentToken ? 'vorhanden' : 'nicht vorhanden');
  
  if (currentToken) {
    try {
      const payload = JSON.parse(atob(currentToken.split('.')[1]));
      console.log('📋 Token Payload:', payload);
      console.log('⏰ Token läuft ab:', new Date(payload.exp * 1000).toLocaleString());
    } catch (error) {
      console.error('❌ Fehler beim Decodieren des Tokens:', error);
    }
  }
  
  // Refresh-Token anzeigen
  const refreshToken = getRefreshToken();
  console.log('🔄 Refresh-Token:', refreshToken ? 'vorhanden' : 'nicht vorhanden');
  
  // Token-Refresh testen
  console.log('🔄 Starte Token-Refresh...');
  const refreshSuccess = await refreshAccessToken();
  console.log('✅ Token-Refresh erfolgreich:', refreshSuccess);
  
  if (refreshSuccess) {
    const newToken = getAccessToken();
    console.log('📋 Neuer Token:', newToken ? 'vorhanden' : 'nicht vorhanden');
    console.log('📋 Neue Token-Länge:', newToken ? newToken.length : 0);
    
    if (newToken) {
      try {
        const payload = JSON.parse(atob(newToken.split('.')[1]));
        console.log('📋 Neuer Token Payload:', payload);
        console.log('⏰ Neuer Token läuft ab:', new Date(payload.exp * 1000).toLocaleString());
      } catch (error) {
        console.error('❌ Fehler beim Decodieren des neuen Tokens:', error);
      }
    }
  }
}; 





 